import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Search, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import api from '@/utils/api';
import { useCart } from '@/context/CartContext';
import { formatGs, productImg } from '@/utils/helpers';
import { toast } from 'sonner';

export default function MenuPage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('category');
  const { branchId, addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/categorias?active=true').then(r => setCategories(r.data.categorias)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    let url = branchId ? `/productos?branch_id=${branchId}&active=true` : '/productos?active=true';
    if (activeCat) url += `&category_id=${activeCat}`;
    api.get(url).then(r => setProducts(r.data.productos)).catch(() => {}).finally(() => setLoading(false));
  }, [branchId, activeCat]);

  const filtered = search
    ? products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    : products;

  const grouped = {};
  filtered.forEach(p => {
    const cat = p.category_name || 'Otros';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  });

  const hasVariants = (p) => p.variant_required || (parseInt(p.variant_count) || 0) > 0;

  const quickAdd = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasVariants(product)) {
      navigate(`/producto/${product.id}`);
      return;
    }
    const price = product.current_price ? parseFloat(product.current_price) : (product.branch_price ? parseFloat(product.branch_price) : 0);
    const { wasUpdate, quantity } = addItem(product, 1, [], price);
    toast(wasUpdate ? `Cantidad actualizada: x${quantity}` : `Agregado al carrito: ${product.name}`, {
      description: wasUpdate ? product.name : `x1`,
      duration: 2500,
      action: { label: 'Ver carrito', onClick: () => navigate('/carrito') },
    });
  };

  return (
    <div data-testid="menu-page" className="pb-nav">
      {/* Search */}
      <div className="px-4 pt-4 pb-2">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="menu-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar productos..."
            className="bg-zinc-900 border-zinc-800 h-11 pl-10"
          />
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide">
        <button
          data-testid="menu-cat-all"
          onClick={() => setSearchParams({})}
          className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${!activeCat ? 'bg-[#E84C1C] text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
        >
          Todos
        </button>
        {categories.map(c => (
          <button
            key={c.id}
            data-testid={`menu-cat-${c.id}`}
            onClick={() => setSearchParams({ category: c.id })}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${String(activeCat) === String(c.id) ? 'bg-[#E84C1C] text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Products */}
      {loading ? (
        <div className="grid grid-cols-2 gap-3 px-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-zinc-900/50 rounded-xl overflow-hidden animate-pulse">
              <div className="aspect-[4/3] bg-zinc-800" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-zinc-800 rounded w-3/4" />
                <div className="h-4 bg-zinc-800 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-4 space-y-6">
          {Object.entries(grouped).map(([catName, prods]) => (
            <div key={catName}>
              {!activeCat && <h3 className="text-base font-semibold text-zinc-300 mb-3">{catName}</h3>}
              <div className="grid grid-cols-2 gap-3">
                {prods.map(p => (
                  <Link
                    key={p.id}
                    to={`/producto/${p.id}`}
                    data-testid={`menu-product-${p.id}`}
                    className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden hover:border-[#E84C1C]/40 transition-colors group relative"
                  >
                    {productImg(p.image_url) ? (
                      <div className="aspect-[4/3] overflow-hidden bg-zinc-800">
                        <img src={productImg(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] bg-zinc-800/50 flex items-center justify-center">
                        <span className="text-zinc-600 text-3xl font-bold">{p.name[0]}</span>
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-zinc-500 truncate mt-0.5">{p.description}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div>
                          {hasVariants(p) ? (
                            <p className="text-zinc-400 text-xs font-normal">Ver opciones</p>
                          ) : p.promo_active ? (
                            <>
                              <span className="text-zinc-500 text-xs line-through mr-1">{formatGs(p.branch_price)}</span>
                              <span className="text-[#E84C1C] text-sm font-bold">{formatGs(p.current_price)}</span>
                            </>
                          ) : (
                            <p className="text-[#E84C1C] text-sm font-bold">
                              {p.branch_price ? formatGs(p.branch_price) : <span className="text-zinc-500 text-xs font-normal">Ver precio</span>}
                            </p>
                          )}
                          {p.promo_label && <p className="text-[10px] text-[#E84C1C]/80 mt-0.5">{p.promo_label}</p>}
                        </div>
                        {hasVariants(p) ? (
                          <button
                            data-testid={`menu-variants-${p.id}`}
                            onClick={(e) => quickAdd(e, p)}
                            className="flex items-center gap-0.5 text-[10px] font-medium text-[#E84C1C] bg-[#E84C1C]/10 px-2.5 py-1.5 rounded-full hover:bg-[#E84C1C]/20 active:scale-90 transition-all"
                          >
                            Elegir <ChevronRight size={12} />
                          </button>
                        ) : (
                          <button
                            data-testid={`menu-add-${p.id}`}
                            onClick={(e) => quickAdd(e, p)}
                            className="w-8 h-8 rounded-full bg-[#E84C1C] text-white flex items-center justify-center hover:bg-[#d43f12] active:scale-90 transition-all"
                          >
                            <Plus size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-zinc-500 py-12">No se encontraron productos.</p>
          )}
        </div>
      )}
    </div>
  );
}
